import React, { useState } from 'react';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { LANGUAGES } from '../util/languages';

type InputProps = {
  value: string;
  label: string;
  size?: 'small' | 'medium';
  onChange: (value: string) => void;
};

export const LanguageSelector = ({ value, label, size, onChange }: InputProps) => {
  const [isSelectingOther, setIsSelectingOther] = useState<boolean>(false);

  const handleSelectSelection = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const newValue = event.target.value;
    if (typeof newValue === 'string') {
      if (newValue === 'other') {
        setIsSelectingOther(true);
      } else {
        onChange(newValue);
        setIsSelectingOther(false);
      }
    }
  };

  const handleAutocompleteSelection = (
    event: React.ChangeEvent<{}>,
    newValue: [string, string] | null,
  ) => {
    onChange(newValue ? newValue[0] : '');
    setIsSelectingOther(false);
  };

  // This is a little more involved than we usually have to be to get a field to autofocus
  // The Autocomplete doesn't have an autoFocus property yet, and when you put autoFocus
  // on the TextField it doesn't seem to work, seemingly because the timing involved
  // with rendering the TextField.
  // This little piece of code gets the job done, but is a little hacky, because it relies
  // on the fact that there is one input rendered for the TextField. I also had to put the
  // focus at the end of the stack with setTimeout, which is never great.
  const focusUsernameInputField = (textFieldRef: HTMLDivElement | null) => {
    if (!textFieldRef) {
      return;
    }

    const inputBoxRefs = textFieldRef.getElementsByTagName('input');
    setTimeout(() => {
      inputBoxRefs[0].focus();
    }, 100);
  };

  const isValueInFirstSelect = value === '' || value === 'en' || value === 'es';
  const selectValue = isValueInFirstSelect ? value : 'other';
  const selectItems = [
    <MenuItem key="en" value="en">
      {LANGUAGES.en}
    </MenuItem>,
    <MenuItem key="es" value="es">
      {LANGUAGES.es}
    </MenuItem>,
    <MenuItem key="other" value="other">
      Other{!isValueInFirstSelect ? ` - ${LANGUAGES[value]}` : ''}
    </MenuItem>,
  ];

  const autocompleteItems = Object.entries(LANGUAGES);

  return (
    <FormControl fullWidth margin="dense">
      <InputLabel htmlFor="langue-select" shrink>
        {label}
      </InputLabel>
      <Select
        value={isSelectingOther ? 'other' : selectValue}
        onChange={handleSelectSelection}
        margin="dense"
        inputProps={{
          name: 'languagePreference',
          id: 'langue-select',
        }}
      >
        {selectItems}
      </Select>
      {isSelectingOther ? (
        <Autocomplete
          options={autocompleteItems}
          getOptionLabel={(option) => option[1]}
          onChange={handleAutocompleteSelection}
          size={size}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Language"
              margin="normal"
              variant="outlined"
              ref={focusUsernameInputField}
              required
            />
          )}
        />
      ) : null}
    </FormControl>
  );
};
